import { cn } from "cn";
import { CheckIcon } from "lucide-react";
import type React from "react";
import { createContext, useContext, useRef, useState } from "react";
import { ChevronDownIcon } from "#/components/AnimatedIcons/ChevronDown";
import { Button } from "#/components/Button/Button";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "#/components/Command/Command";
import type { SelectFilterOption } from "#/components/Filter/SelectFilter";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/Popover/Popover";

type ComboboxContextProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	value: string | undefined;
	onValueChange: ((value: string | undefined) => void) | undefined;
};

const ComboboxContext = createContext<ComboboxContextProps | null>(null);

function useCombobox() {
	const context = useContext(ComboboxContext);
	if (!context) {
		throw new Error("useCombobox must be used within a <Combobox />");
	}
	return context;
}

type ComboboxProps = {
	value?: string;
	onValueChange?: (value: string | undefined) => void;
} & React.ComponentProps<typeof Popover>;

export const Combobox = ({
	children,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	value,
	onValueChange,
	...props
}: ComboboxProps) => {
	const [internalOpen, setInternalOpen] = useState(false);

	// Use controlled state if provided, otherwise use internal state
	const open = controlledOpen ?? internalOpen;
	const setOpen = controlledOnOpenChange ?? setInternalOpen;

	return (
		<ComboboxContext.Provider value={{ open, setOpen, value, onValueChange }}>
			<Popover open={open} onOpenChange={setOpen} {...props}>
				{children}
			</Popover>
		</ComboboxContext.Provider>
	);
};

export const ComboboxTrigger = PopoverTrigger;

type ComboboxButtonProps = React.ComponentProps<"button"> & {
	width?: number;
	selectedOption?: SelectFilterOption;
	placeholder?: string;
};

export const ComboboxButton = ({
	children,
	className,
	width,
	selectedOption,
	placeholder,
	ref,
	...props
}: ComboboxButtonProps) => {
	return (
		<Button
			className="flex items-center justify-between shrink-0 grow gap-2 pr-1.5"
			style={{ flexBasis: width }}
			variant="outline"
			ref={ref}
			{...props}
		>
			{selectedOption?.startIcon}
			<span className="text-left block overflow-hidden text-ellipsis grow">
				{selectedOption?.label ?? placeholder}
			</span>
			<ChevronDownIcon className="size-icon-sm" />
		</Button>
	);
};

type ComboboxContentProps = React.ComponentProps<typeof PopoverContent> & {
	shouldFilter?: boolean;
};

export const ComboboxContent = ({
	children,
	className,
	ref,
	shouldFilter,
	onOpenAutoFocus,
	...props
}: ComboboxContentProps) => {
	const commandRef = useRef<HTMLDivElement>(null);

	return (
		<PopoverContent
			ref={ref}
			className={cn(
				"flex w-auto flex-col overflow-y-hidden border-border text-sm",
				className,
			)}
			onOpenAutoFocus={(event) => {
				onOpenAutoFocus?.(event);
				// Without a search input, focus would land on the popover itself,
				// where the arrow keys never reach the command list.
				const root = commandRef.current;
				if (!event.defaultPrevented && !root?.querySelector("[cmdk-input]")) {
					event.preventDefault();
					root?.querySelector<HTMLElement>("[cmdk-list]")?.focus();
				}
			}}
			{...props}
		>
			<Command
				ref={commandRef}
				shouldFilter={shouldFilter}
				className="min-h-0 flex-1"
			>
				{children}
			</Command>
		</PopoverContent>
	);
};

export const ComboboxInput = CommandInput;

export const ComboboxList: React.FC<
	React.ComponentProps<typeof CommandList>
> = ({ className, ...props }) => {
	return (
		<CommandList
			className={cn("max-h-none min-h-0 flex-1 p-2 outline-hidden", className)}
			{...props}
		/>
	);
};

export const ComboboxItem = ({
	children,
	className,
	onSelect,
	value,
	...props
}: React.ComponentProps<typeof CommandItem>) => {
	const { setOpen, value: selectedValue, onValueChange } = useCombobox();
	const isSelected = value === selectedValue;

	return (
		<CommandItem
			value={value}
			className={className}
			onSelect={(itemValue) => {
				setOpen(false);
				// Toggle behavior: selecting the same value deselects it.
				const newValue = itemValue === selectedValue ? undefined : itemValue;
				onValueChange?.(newValue);
				onSelect?.(itemValue);
			}}
			{...props}
		>
			{children}
			<CheckIcon
				className={cn(
					"ml-2 size-4 min-w-0 shrink-0",
					isSelected ? "opacity-100" : "opacity-0",
				)}
			/>
		</CommandItem>
	);
};

export const ComboboxEmpty = CommandEmpty;
